import React, { useState, useEffect } from 'react';
import { X, Search, Download, ExternalLink, Wand2, Save, Trash2, Loader2, Edit3 } from 'lucide-react';

// Component to render SVG content
const SvgRenderer: React.FC<{ data: string; className?: string }> = ({ data, className = "" }) => {
  // Now the backend returns SVG content directly, so we can render it immediately
  if (!data) {
    return (
      <div className={`flex items-center justify-center text-muted-foreground ${className}`}>
        <X size={24} />
      </div>
    );
  }

  // Log for debugging
  // console.log('🔍 [SVG RENDERER] Processing SVG, length:', data.length); // Removed for production
  // console.log('🔍 [SVG RENDERER] First 100 chars:', data.substring(0, 100)); // Removed for production
  
  // Process the SVG to ensure proper scaling and remove background
  // Use the exact same logic as the backend
  let processedSvg = data
    // Remove any background elements (rect or path with white/light fills)
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(254,254,254\)["'][^>]*\/?>/gi, '')
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']#ffffff["'][^>]*\/?>/gi, '')
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']white["'][^>]*\/?>/gi, '')
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(255,255,255\)["'][^>]*\/?>/gi, '')
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(255,255,254\)["'][^>]*\/?>/gi, '')
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(254,255,255\)["'][^>]*\/?>/gi, '')
    // Remove any background style attributes
    .replace(/\s*background\s*:\s*[^;"]*;?/gi, '')
    .replace(/\s*background-color\s*:\s*[^;"]*;?/gi, '');

  // Count white fills before and after
  const originalWhiteFills = (data.match(/fill\s*=\s*["']rgb\(254,254,254\)["']/g) || []).length;
  const processedWhiteFills = (processedSvg.match(/fill\s*=\s*["']rgb\(254,254,254\)["']/g) || []).length;
  
  // console.log('🔍 [SVG RENDERER] Original white fills:', originalWhiteFills); // Removed for production
  // console.log('🔍 [SVG RENDERER] Processed white fills:', processedWhiteFills); // Removed for production
  // console.log('🔍 [SVG RENDERER] Length change:', data.length, '->', processedSvg.length); // Removed for production
  
  processedSvg = processedSvg
    .replace(
      /<svg([^>]*)>/i, 
      (match, attributes) => {
        // Extract existing attributes
        const hasViewBox = /viewBox=/i.test(attributes);
        const hasWidth = /width=/i.test(attributes);
        const hasHeight = /height=/i.test(attributes);
        
        // Remove existing width and height if they exist
        let cleanAttributes = attributes
          .replace(/\s*width\s*=\s*["'][^"']*["']/gi, '')
          .replace(/\s*height\s*=\s*["'][^"']*["']/gi, '');
        
        // Add viewBox if it doesn't exist (assume 1024x1024 for Replicate SVGs)
        if (!hasViewBox) {
          cleanAttributes += ' viewBox="0 0 1024 1024"';
        }
        
        // Add responsive styling with transparent background
        const style = 'style="width: 100%; height: 100%; max-width: 100%; max-height: 100%; background: transparent;"';
        
        return `<svg${cleanAttributes} ${style}>`;
      }
    );

  return (
    <div 
      className={`${className} flex items-center justify-center overflow-hidden`}
      dangerouslySetInnerHTML={{ __html: processedSvg }}
    />
  );
};

// Types
interface SvgIcon {
  id: string;
  url: string;
  thumbnail: string;
  photographer: string;
  photographerUrl: string;
  width: number;
  height: number;
  alt: string;
  source: 'freepik' | 'noun';
}

interface GeneratedIcon {
  id: string;
  filename: string;
  data: string;
  tags: string[];
  prompt: string;
  status: 'generating' | 'completed' | 'error';
  error?: string;
}

interface StatemiaIcon {
  id: string;
  filename: string;
  data: string;
  tags: string[];
  source: 'ai' | 'upload';
  prompt?: string;
  createdAt: string;
}

interface IconSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (icon: SvgIcon | StatemiaIcon) => void;
  initialQuery?: string;
}

const IconSearchModal: React.FC<IconSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialQuery = ''
}) => {
  // State for tabs
  const [activeTab, setActiveTab] = useState<'search' | 'generate'>('search');
  
  // State for search tab
  const [query, setQuery] = useState(initialQuery);
  const [searching, setSearching] = useState(false);
  const [icons, setIcons] = useState<SvgIcon[]>([]);
  const [statemiaIcons, setStatemiaIcons] = useState<StatemiaIcon[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // State for source selection
  const [selectedSources, setSelectedSources] = useState<{
    statemia: boolean;
    freepik: boolean;
    noun: boolean;
  }>({
    statemia: true,
    freepik: true,
    noun: false
  });
  
  // State for generate tab
  const [generateQuery, setGenerateQuery] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedIcons, setGeneratedIcons] = useState<GeneratedIcon[]>([]);
  
  // State for save confirmation modal
  const [saveModal, setSaveModal] = useState<{
    isOpen: boolean;
    icon: GeneratedIcon | null;
    filename: string;
    tags: string[];
    tagsInput: string; // Raw input for tags
  }>({
    isOpen: false,
    icon: null,
    filename: '',
    tags: [],
    tagsInput: ''
  });

  // Initialize with default query
  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
      setGenerateQuery(initialQuery);
    }
  }, [initialQuery]);

  // Handle source selection
  const handleSourceChange = (source: keyof typeof selectedSources) => {
    setSelectedSources(prev => ({
      ...prev,
      [source]: !prev[source]
    }));
  };

  // Search function
  const handleSearch = async () => {
    if (!query.trim()) return;
    
    setSearching(true);
    setError(null);
    setIcons([]);
    setStatemiaIcons([]);
    
    try {
      const searchPromises = [];
      
      // Search in Statemia database if selected
      if (selectedSources.statemia) {
        searchPromises.push(
          fetch(`/api/icons/search?q=${encodeURIComponent(query)}&sources=ai,upload`)
            .then(res => res.json())
            .then(data => {
              if (data.success) {
                setStatemiaIcons(data.data || []);
              }
            })
            .catch(err => console.error('Error searching Statemia icons:', err))
        );
      }
      
      // Search in Freepik if selected
      if (selectedSources.freepik) {
        searchPromises.push(
          fetch(`/api/images/stock/search?query=${encodeURIComponent(query)}&source=freepik&perPage=20&context=chart-icons`)
            .then(res => res.json())
            .then(data => {
              if (data.images) {
                setIcons(prev => [...prev, ...data.images]);
              }
            })
            .catch(err => console.error('Error searching Freepik icons:', err))
        );
      }
      
      // Search in Noun Project if selected
      if (selectedSources.noun) {
        searchPromises.push(
          fetch(`/api/images/stock/search?query=${encodeURIComponent(query)}&source=noun&perPage=20&context=chart-icons`)
            .then(res => res.json())
            .then(data => {
              if (data.images) {
                setIcons(prev => [...prev, ...data.images]);
              }
            })
            .catch(err => console.error('Error searching Noun Project icons:', err))
        );
      }
      
      await Promise.all(searchPromises);
      
      // Check if no results found
      if (selectedSources.statemia && statemiaIcons.length === 0 && 
          selectedSources.freepik && selectedSources.noun && icons.length === 0) {
        setError('No se encontraron iconos para tu búsqueda');
      }
      
    } catch (err) {
      console.error('Error searching icons:', err);
      setError('Error al buscar iconos. Intenta de nuevo.');
    } finally {
      setSearching(false);
    }
  };

  // Generate icon function
  const handleGenerate = async () => {
    if (!generateQuery.trim()) return;
    
    setGenerating(true);
    
    try {
      const prompt = `icon style black outline vector of ${generateQuery}`;
      
      const response = await fetch('/api/replicate/generate-icon', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          size: '1024x1024',
          style: 'line_art',
          aspect_ratio: '1:1'
        })
      });
      
      if (!response.ok) {
        throw new Error('Error generating icon');
      }
      
      const data = await response.json();
      
      if (data.success) {
        const newIcon: GeneratedIcon = {
          id: data.data.id,
          filename: `${generateQuery}-${Date.now()}.svg`,
          data: data.data.output,
          tags: generateQuery.split(' ').filter(word => word.length > 2),
          prompt,
          status: 'completed'
        };
        
        setGeneratedIcons(prev => [...prev, newIcon]);
      } else {
        throw new Error(data.message || 'Error generating icon');
      }
      
    } catch (err) {
      console.error('Error generating icon:', err);
      const errorIcon: GeneratedIcon = {
        id: `error-${Date.now()}`,
        filename: `${generateQuery}-error.svg`,
        data: '',
        tags: [],
        prompt: generateQuery,
        status: 'error',
        error: err instanceof Error ? err.message : 'Unknown error'
      };
      
      setGeneratedIcons(prev => [...prev, errorIcon]);
    } finally {
      setGenerating(false);
    }
  };

  // Open save confirmation modal
  const handleSaveIcon = (icon: GeneratedIcon) => {
    setSaveModal({
      isOpen: true,
      icon,
      filename: icon.filename.replace('.svg', ''),
      tags: [...icon.tags],
      tagsInput: icon.tags.join(', ')
    });
  };

  // Confirm save icon
  const handleConfirmSave = async () => {
    if (!saveModal.icon) return;
    
    // Process tags from input
    const finalTags = saveModal.tagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    try {
      const response = await fetch('/api/icons/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: `${saveModal.filename}.svg`,
          data: saveModal.icon.data,
          tags: finalTags,
          prompt: saveModal.icon.prompt,
          source: 'ai'
        })
      });
      
      if (response.ok) {
        // Remove from generated icons
        setGeneratedIcons(prev => prev.filter(i => i.id !== saveModal.icon!.id));
        // Close modal
        setSaveModal({ isOpen: false, icon: null, filename: '', tags: [], tagsInput: '' });
      }
    } catch (err) {
      console.error('Error saving icon:', err);
    }
  };

  // Close save modal
  const handleCloseSaveModal = () => {
    setSaveModal({ isOpen: false, icon: null, filename: '', tags: [], tagsInput: '' });
  };

  // Delete generated icon
  const handleDeleteIcon = (iconId: string) => {
    setGeneratedIcons(prev => prev.filter(icon => icon.id !== iconId));
  };

  // Handle icon selection
  const handleSelect = (icon: SvgIcon | StatemiaIcon) => {
    // Transform the icon to match the expected format
    const transformedIcon = {
      ...icon,
      url: 'url' in icon ? icon.url : `data:image/svg+xml;base64,${btoa(icon.data)}`
    };
    onSelect(transformedIcon);
    onClose();
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (activeTab === 'search') {
        handleSearch();
      } else {
        handleGenerate();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border border-border rounded-lg p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Buscar Icono SVG</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-200 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mb-4">
          <button
            onClick={() => setActiveTab('search')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'search'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Buscar
          </button>
          <button
            onClick={() => setActiveTab('generate')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'generate'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Generar
          </button>
        </div>

        {/* Search Tab */}
        {activeTab === 'search' && (
          <>
            {/* Source Selection */}
            <div className="flex gap-4 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedSources.statemia}
                  onChange={() => handleSourceChange('statemia')}
                  className="rounded border-border"
                />
                <span className="text-sm">Statemia</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedSources.freepik}
                  onChange={() => handleSourceChange('freepik')}
                  className="rounded border-border"
                />
                <span className="text-sm">Freepik</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedSources.noun}
                  onChange={() => handleSourceChange('noun')}
                  className="rounded border-border"
                />
                <span className="text-sm">Noun Project</span>
              </label>
            </div>

            {/* Search Bar */}
            <div className="flex gap-2 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Buscar iconos vectoriales (ej: casa, coche, estrella, persona...)"
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={searching || !query.trim()}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {searching ? 'Buscando...' : 'Buscar'}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            {/* Icons Grid */}
            <div className="flex-1 overflow-y-auto">
              {(icons.length > 0 || statemiaIcons.length > 0) ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {/* Statemia Icons */}
                  {statemiaIcons.map((icon) => (
                    <div
                      key={icon.id}
                      className="group relative border border-border rounded-lg overflow-hidden hover:border-primary transition-colors cursor-pointer"
                      onClick={() => handleSelect(icon)}
                    >
                      <div className="aspect-square bg-transparent flex items-center justify-center p-4">
                        <SvgRenderer 
                          data={icon.data}
                          className="w-full h-full"
                        />
                      </div>
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-white rounded-lg p-2">
                            <ExternalLink size={16} className="text-primary" />
                          </div>
                        </div>
                      </div>

                      {/* Icon Info */}
                      <div className="p-3 bg-background">
                        <p className="text-sm font-medium truncate">{icon.filename}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-muted-foreground capitalize">{icon.source}</span>
                          <span className="text-xs text-muted-foreground">
                            {icon.tags.slice(0, 2).join(', ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* External Icons (Freepik, Noun) */}
                  {icons.map((icon) => (
                    <div
                      key={icon.id}
                      className="group relative border border-border rounded-lg overflow-hidden hover:border-primary transition-colors cursor-pointer"
                      onClick={() => handleSelect(icon)}
                    >
                      <div className="aspect-square bg-surface-100 flex items-center justify-center p-4">
                        <img
                          src={icon.thumbnail}
                          alt={icon.alt}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-white rounded-lg p-2">
                            <ExternalLink size={16} className="text-primary" />
                          </div>
                        </div>
                      </div>

                      {/* Icon Info */}
                      <div className="p-3 bg-background">
                        <p className="text-sm font-medium truncate">{icon.alt}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-muted-foreground capitalize">{icon.source}</span>
                          <a
                            href={icon.photographerUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                          >
                            <ExternalLink size={12} />
                            Autor
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : !searching && query && (
                <div className="text-center py-8 text-muted-foreground">
                  <Search size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No se encontraron iconos para "{query}"</p>
                  <p className="text-sm">Intenta con otros términos de búsqueda</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Generate Tab */}
        {activeTab === 'generate' && (
          <>
            {/* Generate Input */}
            <div className="flex gap-2 mb-4">
              <div className="flex-1 relative">
                <Wand2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                <input
                  type="text"
                  value={generateQuery}
                  onChange={(e) => setGenerateQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Describe el icono (ej: swimming pool, mobile, person, car...)"
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <button
                onClick={handleGenerate}
                disabled={generating || !generateQuery.trim()}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {generating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Wand2 size={16} />
                    Generar
                  </>
                )}
              </button>
            </div>

            {/* Generated Icons Grid */}
            <div className="flex-1 overflow-y-auto">
              {generatedIcons.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {generatedIcons.map((icon) => (
                    <div
                      key={icon.id}
                      className="group relative border border-border rounded-lg overflow-hidden hover:border-primary transition-colors"
                    >
                      <div className="aspect-square bg-transparent flex items-center justify-center p-4">
                        {icon.status === 'generating' ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 size={24} className="animate-spin text-primary" />
                            <span className="text-sm text-muted-foreground">Generando...</span>
                          </div>
                        ) : icon.status === 'error' ? (
                          <div className="flex flex-col items-center gap-2 text-red-500">
                            <X size={24} />
                            <span className="text-sm">Error</span>
                          </div>
                        ) : (
                          <SvgRenderer 
                            data={icon.data}
                            className="w-full h-full"
                          />
                        )}
                      </div>
                      
                      {/* Actions */}
                      {icon.status === 'completed' && (
                        <div className="absolute top-2 right-2 flex gap-1">
                          <button
                            onClick={() => handleSaveIcon(icon)}
                            className="p-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                            title="Guardar icono"
                          >
                            <Save size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteIcon(icon.id)}
                            className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                            title="Eliminar icono"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}

                      {/* Icon Info */}
                      <div className="p-3 bg-background">
                        <p className="text-sm font-medium truncate">{icon.filename}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-muted-foreground">AI Generated</span>
                          <span className="text-xs text-muted-foreground">
                            {icon.tags.slice(0, 2).join(', ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : !generating && (
                <div className="text-center py-8 text-muted-foreground">
                  <Wand2 size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Genera iconos personalizados con IA</p>
                  <p className="text-sm">Describe el icono que necesitas y lo crearemos para ti</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>Iconos proporcionados por</span>
              <a
                href="https://www.freepik.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
              >
                <ExternalLink size={12} />
                Freepik
              </a>
              <span>y</span>
              <a
                href="https://thenounproject.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
              >
                <ExternalLink size={12} />
                The Noun Project
              </a>
            </div>
            <span>Haz clic en un icono para seleccionarlo</span>
          </div>
        </div>
      </div>

      {/* Save Confirmation Modal */}
      {saveModal.isOpen && saveModal.icon && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-background border border-border rounded-lg p-6 w-full max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Edit3 size={20} />
                Guardar Icono
              </h3>
              <button
                onClick={handleCloseSaveModal}
                className="p-2 hover:bg-surface-200 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Icon Preview */}
            <div className="mb-4">
              <div className="aspect-square bg-transparent rounded-lg p-4 flex items-center justify-center">
                <SvgRenderer 
                  data={saveModal.icon.data}
                  className="w-24 h-24"
                />
              </div>
            </div>

            {/* Filename Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Nombre del archivo</label>
              <input
                type="text"
                value={saveModal.filename}
                onChange={(e) => setSaveModal(prev => ({ ...prev, filename: e.target.value }))}
                placeholder="nombre-del-icono"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Tags Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Tags (separados por comas)</label>
              <input
                type="text"
                value={saveModal.tagsInput}
                onChange={(e) => {
                  setSaveModal(prev => ({ 
                    ...prev, 
                    tagsInput: e.target.value
                  }));
                }}
                placeholder="icono, vector, outline, mobile"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Los tags ayudan a encontrar el icono en búsquedas futuras
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleCloseSaveModal}
                className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-surface-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSave}
                disabled={!saveModal.filename.trim()}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                <Save size={16} />
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IconSearchModal;
